import styled, { css } from "styled-components";

import {
    FRAME_HOVER_BACKGROUND_COLOR,
    onBackgroundImage,
} from "components/common/backgroundFrame";
import { CSS_VARIABLES } from "enums/cssVariables";

import {
    BasicCaseProposalStyleProps,
    ProposalDetailStyleProps,
    ProposalNameStyleProps,
    ProposalTextStyleProps,
} from "../types";
import {
    VOTEBOX_BORDER_WIDTH,
    VOTEBOX_DISABLED_BORDER_COLOR,
    VOTEBOX_DISABLED_TEXT_COLOR,
    VOTEBOX_FONT_SIZE_BODY,
    VOTEBOX_FONT_SIZE_CAPTION,
    VOTEBOX_FONT_SIZE_SMALL,
    VOTEBOX_FONT_WEIGHT_BOLD,
    VOTEBOX_FONT_WEIGHT_MEDIUM,
    VOTEBOX_FONT_WEIGHT_REGULAR,
    VOTEBOX_FONT_WEIGHT_SEMIBOLD,
    VOTEBOX_HOVER_BACKGROUND_COLOR,
    VOTEBOX_MUTED_BACKGROUND_COLOR,
    VOTEBOX_NEUTRAL_BORDER_COLOR,
    VOTEBOX_OPTION_MIN_HEIGHT,
    VOTEBOX_OPTION_RADIUS,
    VOTEBOX_ROW_GAP,
    VOTEBOX_SECONDARY_TEXT_COLOR,
    VOTEBOX_TEXT_COLOR,
    VOTEBOX_WHITE,
} from "../voteboxTokens";
import { getSelectionColor } from "../services/getSelectionColor";
import { BaseOptionButton } from "./BaseOptionButton";

// Side by side, the box sits next to the text, as in the mockup. When a text of the line no longer
// fits next to its box (phone, 4 options), every box of the line goes on top of its text.
const compactButton = css<BasicCaseProposalStyleProps>`
    flex-direction: ${(props) =>
        "top" === props.$boxPosition ? "column" : "row"};
    gap: 4px 6px;
    /* Shrinks to its share of the line instead of overflowing it, so a text that no longer fits is detected. */
    min-width: 0;
    padding: 6px 8px;
`;

export const StyledButton = styled(
    BaseOptionButton,
)<BasicCaseProposalStyleProps>`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: ${VOTEBOX_ROW_GAP};
    min-height: ${VOTEBOX_OPTION_MIN_HEIGHT};
    padding: 8px 12px;
    ${(props) => props.$isCompact && compactButton}
    border: ${VOTEBOX_BORDER_WIDTH} solid
        ${(props) =>
        props.$isActive
            ? getSelectionColor(props.$color)
            : props.$color || VOTEBOX_NEUTRAL_BORDER_COLOR};
    border-radius: ${VOTEBOX_OPTION_RADIUS};
    background: ${(props) =>
        props.$isActive
            ? getSelectionColor(props.$color)
            : `var(${CSS_VARIABLES.UNSELECTED_OPTION_BACKGROUND_COLOR}, transparent)`};
    color: ${(props) =>
        props.$isActive ? VOTEBOX_WHITE : props.$color || VOTEBOX_TEXT_COLOR};
    font-size: ${(props) =>
        props.$isCompact ? VOTEBOX_FONT_SIZE_SMALL : VOTEBOX_FONT_SIZE_BODY};
    font-weight: ${(props) =>
        props.$isActive
            ? VOTEBOX_FONT_WEIGHT_BOLD
            : VOTEBOX_FONT_WEIGHT_MEDIUM};
    text-align: center;
    &:hover:not([disabled]) {
        background: ${(props) =>
            props.$isActive
                ? getSelectionColor(props.$color)
                : props.$color || VOTEBOX_HOVER_BACKGROUND_COLOR};
        color: ${(props) =>
            props.$isActive || props.$color
                ? VOTEBOX_WHITE
                : VOTEBOX_TEXT_COLOR};
    }
    /* Uncolored options (election candidates): the hover, sticky on mobile after a tap, must not turn white in the frame. */
    ${(props) =>
        !props.$isActive &&
        !props.$color &&
        onBackgroundImage(css`
            &:hover:not([disabled]) {
                background: ${FRAME_HOVER_BACKGROUND_COLOR};
            }
        `)}
    /* Once the vote is sent, the chosen option keeps its look and the others are greyed. */
    &[disabled] {
        background: ${(props) =>
            props.$isActive
                ? getSelectionColor(props.$color)
                : VOTEBOX_MUTED_BACKGROUND_COLOR};
        border-color: ${(props) =>
            props.$isActive
                ? getSelectionColor(props.$color)
                : VOTEBOX_DISABLED_BORDER_COLOR};
        color: ${(props) =>
            props.$isActive ? VOTEBOX_WHITE : VOTEBOX_DISABLED_TEXT_COLOR};
        cursor: ${(props) => (props.$isActive ? "default" : "not-allowed")};
    }
`;

export const ProposalImage = styled.img<ProposalDetailStyleProps>`
    flex: none;
    width: 56px;
    height: 56px;
    object-fit: cover;
    border-radius: ${VOTEBOX_OPTION_RADIUS};
    opacity: ${(props) => (props.$isDisabled && !props.$isActive ? 0.6 : 1)};
`;

// Side by side, the text keeps its own width, so that the box and the text are centered together.
const compactText = css`
    flex: 0 1 auto;
    max-width: 100%;
`;

// Centered texts are shifted by the box width, so they look centered in the whole option.
export const TextWrapper = styled.span<ProposalTextStyleProps>`
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
    padding-inline-end: ${(props) => (props.$hasBoxOffset ? "24px" : "0")};
    text-align: ${(props) => (props.$isTextAlignedStart ? "start" : "center")};
    /* A word is only cut when it cannot fit alone on a line. */
    overflow-wrap: break-word;
    white-space: normal;
    ${(props) => props.$isCompact && compactText}
`;

export const StyledText = styled.span<ProposalNameStyleProps>`
    font-weight: ${(props) => {
        if (!props.$isRich) {
            return "inherit";
        }

        return props.$isActive
            ? VOTEBOX_FONT_WEIGHT_BOLD
            : VOTEBOX_FONT_WEIGHT_SEMIBOLD;
    }};
`;

export const DescriptionText = styled.span<ProposalDetailStyleProps>`
    font-size: ${VOTEBOX_FONT_SIZE_CAPTION};
    font-weight: ${VOTEBOX_FONT_WEIGHT_REGULAR};
    line-height: 1.35;
    color: ${(props) => {
        if (props.$isActive) {
            return VOTEBOX_WHITE;
        }

        if (props.$isDisabled) {
            return VOTEBOX_DISABLED_TEXT_COLOR;
        }

        return VOTEBOX_SECONDARY_TEXT_COLOR;
    }};
`;
